// Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import * as Sentry from '@sentry/browser'

import {
  Button,
  Modal,
  TextArea,
  SvgIcon,
  ExpandableOverviewItem
} from '../'


export default class CreditApplicationReview extends Component {
  static propTypes = {
    creditApplication: PropTypes.string.isRequired,
    creditApplicationStatus: PropTypes.string.isRequired,
    approvedBy: PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired
    }),
    finalisedBy: PropTypes.shape({
      id: PropTypes.string
    }),
    status: PropTypes.string,
    assignedToName: PropTypes.string,
    approverNotes: PropTypes.string,
    updateCreditApplication: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  state = {
    showNotesModal: false,
    approverNotes: this.props.approverNotes
  }

  handleRejectClick = () => {
    this.setState({ showNotesModal: true })
  }

  handleApproveClick = () => {
    this.setState({ approveInProgress: true })

    this.props
      .updateCreditApplication({
        approvedBy: localStorage.getItem("userUUID"),
        approvedAt: moment.utc().format("YYYY-MM-DD HH:mm:ss.SSS"),
        status: this.getStatusToSave(),
        assignedTo: this.props.finalisedBy.id,
      })
      .then(() => {
        this.props.onComplete();
        this.setState({ approveInProgress: false });
      })
      .catch((error) => {
        Sentry.captureMessage(`Failed to approve loan application - ${error}`);
        this.setState({ approveInProgress: false });
      });
  }

  saveApproverNotes = () => {
    if (this.state.approverNotes !== this.props.approverNotes) {
      this.props.updateCreditApplication({
        approverNotes: this.state.approverNotes,
        assignedTo: this.props.finalisedBy.id,
        status: 'incomplete'
      })
      .then(() => {
        console.log('loan application approver notes save successful')
        this.props.onRejected()
      })
      .catch(error => console.log('Failed to update loan application approver notes', error))
    } else {
      this.props.onRejected()
    }
  }

  getStatusToSave = () => {
    let outcome = this.props.outcome
    if (['QUALIFIED_PARTIALLY', 'QUALIFIED_FULLY'].indexOf(outcome) > -1) {
      return 'approved'
    } else if (['REFERRED_TODC_INTERESTED', 'REFERRED_TODC_NOTINTERESTED', 'DECLINED', 'EXPIRED', 'CANCELLED'].indexOf(outcome) > -1) {
      return 'finalised_application'
    }
  }

  getSummary = (userIsReviewer, reviewerName, finalisedBy, approved, approvedFeedback) => {
    if (userIsReviewer && !approved) {
      return 'Review pending'
    } else if (reviewerName) {
      return `Review pending by ${reviewerName}`
    } else if (!finalisedBy) {
      return 'Not finalised'
    } else if (approved){
      return approvedFeedback
    } else {
      return 'Not approved yet'
    }
  }

  render() {
    let { status, assignedTo, approvedBy, approvedAt, finalisedBy, approverNotes, open, onClick, className } = this.props
    let userIsFinaliser = finalisedBy && finalisedBy.id === localStorage.getItem('userUUID')
    let userIsReviewer = assignedTo && assignedTo.id === localStorage.getItem('userUUID')
    let approved = approvedBy !== null
    let reviewerName = status === 'UNDER_REVIEW' && !userIsReviewer
      ? `${assignedTo.firstName} ${assignedTo.lastName}`
      : null
    let approvedFeedback = approved
      ? `Approved by ${approvedBy.firstName} ${approvedBy.lastName}${approvedAt ? ` at ${moment(approvedAt).format('lll')}` : ''}`
      : null

    return (
      <ExpandableOverviewItem
        title='Review'
        summary={this.getSummary(userIsReviewer, reviewerName, finalisedBy, approved, approvedFeedback)}
        iconSymbol={approved ? 'check' : null}
        iconColour={approved ? 'success' : null}
        open={open}
        locked={userIsFinaliser || !finalisedBy}
        onClick={onClick}
        className={className}>
        {approved && approverNotes
          ? <TextArea
              label='Notes'
              value={approverNotes}
              onChange={e => this.setState({ approverNotes: e.target.value })}
              disabled={true}
              rows={5}
            />
          : <React.Fragment>
              <div className='slds-m-bottom_medium'>No approver notes.</div>
              {approvedFeedback &&
                <div className='slds-m-top_small slds-text-color_weak slds-m-bottom_medium'>
                  <SvgIcon
                    variant='utility'
                    symbol='info'
                    colour='light'
                    size='small'
                    className='slds-m-right_small'
                  />
                  {approvedFeedback}
                </div>
              }
            </React.Fragment>
        }
        {!approved && userIsReviewer &&
          <div id='rejectOrValidate' className='slds-m-left_large slds-m-bottom_large'>
            <Button
              type='brand'
              onClick={this.handleRejectClick}
              disabled={this.state.approveInProgress}
              minWidth={120}>
              <SvgIcon
                variant='utility'
                symbol='close'
                colour='inverse'
                size='x-small'
                buttonIcon={true}
                className='slds-button__icon_left'
              />
              Reject
            </Button>
            <Button
              type='brand'
              onClick={this.handleApproveClick}
              disabled={this.state.approveInProgress}
              minWidth={120}>
              <SvgIcon
                variant='utility'
                symbol='check'
                colour='inverse'
                size='x-small'
                buttonIcon={true}
                className='slds-button__icon_left'
              />
              {this.state.approveInProgress
                ? 'Approving...'
                : 'Approve'
              }
            </Button>
          </div>
        }
        {this.state.showNotesModal &&
          <Modal
            body={
              <div className='slds-p-around_large'>
                <TextArea
                  label='Add notes'
                  value={this.state.approverNotes}
                  onChange={e => this.setState({ approverNotes: e.target.value })}
                  rows={5}
                />
                <div className='slds-align_absolute-center'>
                  <Button
                    className='slds-m-top_medium'
                    minWidth={140}
                    onClick={() => {
                      this.setState({ approverNotes: '', showNotesModal: false })
                    }}>
                    Cancel
                  </Button>
                  <Button
                    type='brand'
                    className='slds-m-top_medium'
                    minWidth={140}
                    onClick={() => {
                      this.saveApproverNotes()
                      this.setState({ showNotesModal: false })
                    }}>
                    Save
                  </Button>
                </div>
              </div>
            }
            elementId='rejectOrValidate'
          />
        }
      </ExpandableOverviewItem>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/creditapplication-review/index.js